import React, { useState, useEffect, useCallback } from 'react';
import revShareService from '../services/revShareService';
import RevShareTierTable from './RevShareTierTable';

const PRODUCTS = [
  { key: 'vault',          label: 'Vault' },
  { key: 'paymentporter',  label: 'Payment Porter' },
  { key: 'sync',           label: 'Sync' },
];

const DefaultRevShare = () => {
  const [activeProduct, setActiveProduct] = useState('vault');
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3000);
  };

  const fetchTiers = useCallback(async (product) => {
    setLoading(true);
    setTiers([]);
    try {
      const res = await revShareService.getDefaultTiers(product);
      setTiers(res.data?.tiers || res.tiers || []);
    } catch {
      showMsg('Failed to load revenue share tiers', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTiers(activeProduct); }, [activeProduct, fetchTiers]);

  const handleTabChange = (product) => {
    if (product === activeProduct) return;
    setActiveProduct(product);
  };

  const handleSave = async (id, data) => {
    setSaving(true);
    try {
      await revShareService.updateDefaultTier(id, data);
      showMsg('Tier updated');
      await fetchTiers(activeProduct);
    } catch {
      showMsg('Failed to update tier', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tier?')) return;
    setSaving(true);
    try {
      await revShareService.deleteDefaultTier(id);
      showMsg('Tier deleted');
      await fetchTiers(activeProduct);
    } catch {
      showMsg('Failed to delete tier', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async (data) => {
    setSaving(true);
    try {
      await revShareService.createDefaultTier(activeProduct, data);
      showMsg('Tier added');
      await fetchTiers(activeProduct);
    } catch {
      showMsg('Failed to add tier', 'error');
    } finally {
      setSaving(false);
    }
  };

  const activeLabel = PRODUCTS.find(p => p.key === activeProduct)?.label || activeProduct;

  return (
    <div style={{ padding: '2rem', maxWidth: '800px' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>Default Revenue Share</h1>
        <p style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem' }}>
          Define default revenue share tiers by product. Partners with no custom tiers will use these.
        </p>
      </div>

      <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: '1.5rem' }}>
        {PRODUCTS.map(p => (
          <button
            key={p.key}
            onClick={() => handleTabChange(p.key)}
            style={{
              padding: '8px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: activeProduct === p.key ? 700 : 400,
              color: activeProduct === p.key ? '#1e40af' : '#64748b',
              borderBottom: activeProduct === p.key ? '2px solid #1e40af' : '2px solid transparent',
              marginBottom: '-2px',
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {msg && (
        <div style={{
          padding: '0.75rem 1rem',
          background: msg.type === 'error' ? '#fee2e2' : '#dcfce7',
          color: msg.type === 'error' ? '#dc2626' : '#166534',
          borderRadius: '6px', marginBottom: '1rem', fontSize: '0.875rem',
        }}>{msg.text}</div>
      )}

      {loading ? (
        <div style={{ color: '#64748b', fontSize: '0.875rem' }}>Loading...</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1.25rem' }}>
          <p style={{ fontSize: '0.8125rem', color: '#64748b', marginTop: 0, marginBottom: '1rem' }}>
            Tiers for <strong>{activeLabel}</strong>. The range is inclusive — a sale when the partner has a count within the From–To range earns the specified %.
          </p>
          <RevShareTierTable
            tiers={tiers}
            onSave={handleSave}
            onDelete={handleDelete}
            onAdd={handleAdd}
            saving={saving}
          />
        </div>
      )}
    </div>
  );
};

export default DefaultRevShare;
